import { useMemo } from "react"
import { useQuery } from "urql"

import "@/screens/CompanySettings/Accounting/graphql/companyAccoutingTerms.list.query"

import {
  type CompanyAccountingTerm,
  CompanyAccountingTermListDocument,
  type CompanyAccountingTermListQuery,
  type CompanyAccountingTermListQueryVariables,
  CompanyAccountingType,
} from "@/graphql/codegen/graphql"

interface UseGetVendorPaymentTermReturn {
  vendorPaymentTermName: string | undefined
  isFetching: boolean
}

export const useGetVendorPaymentTerm = (vendorCompanyId: string | null | undefined): UseGetVendorPaymentTermReturn => {
  const [{ data, fetching }] = useQuery<CompanyAccountingTermListQuery, CompanyAccountingTermListQueryVariables>({
    query: CompanyAccountingTermListDocument,
    variables: { input: { companyId: vendorCompanyId ?? "" } },
    // The accounting terms upsert does not invalidate this query, so refresh it after showing the cached value.
    requestPolicy: "cache-and-network",
    pause: !vendorCompanyId,
  })

  const vendorPaymentTermName = useMemo(() => {
    // AK-7104: a paused query still returns the last response, so a cleared vendor would keep its old term.
    if (!vendorCompanyId) return undefined

    const list = data?.companyAccountingTerm.list
    if (list?.__typename !== "CompanyAccountingTermListSuccess") return undefined

    const accountingTerms = list.companyAccountingTerm as CompanyAccountingTerm[] | null | undefined

    return accountingTerms?.find((term) => term.companyType === CompanyAccountingType.Vendor)?.paymentTerm?.name
  }, [data, vendorCompanyId])

  return { vendorPaymentTermName, isFetching: fetching }
}
